import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../components/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so multiple components mounting in the same
// session don't refetch `/organization/me` on every render. Cleared
// when the auth user changes (login/logout).
let _cache = { userId: null, tier: null, ready: false };
const _subscribers = new Set();

function broadcast() {
  for (const fn of _subscribers) fn(_cache);
}

async function _refresh(userId) {
  try {
    const r = await axios.get(`${API}/organization/me`);
    _cache = {
      userId,
      tier: (r.data?.organization?.license_tier || '').toLowerCase() || 'standard',
      ready: true,
    };
  } catch {
    _cache = { userId, tier: 'standard', ready: true };
  }
  broadcast();
}

/**
 * Returns `{ tier, isPro, ready }` for the active user's organization.
 * `ready` flips to true once the first fetch resolves so consumers can
 * avoid showing a flash-of-Pro-content for a Standard user.
 */
export function useOrgTier() {
  const { user } = useAuth();
  const [snap, setSnap] = useState(_cache);

  useEffect(() => {
    if (!user) return;
    if (_cache.userId !== user.id) {
      _cache = { userId: user.id, tier: null, ready: false };
      _refresh(user.id);
    } else {
      // already fetched for this user — make sure new subscriber sees it
      setSnap(_cache);
    }
    const sub = (next) => setSnap(next);
    _subscribers.add(sub);
    return () => _subscribers.delete(sub);
  }, [user]);

  return {
    tier: snap.tier,
    isPro: snap.tier === 'pro',
    ready: snap.ready,
  };
}

export default useOrgTier;
